/**
 * Ellucian Ethos (Banner SIS) — the integration surface for the read-only
 * student-information lane (CD brief 2026-08-19-0002, overlay kit 7cc9e00e).
 *
 * Why Ethos gets its own module instead of another INTEGRATION_CONFIGS row:
 *
 * 1. **Verify is an onboarding preflight, not a ping.** An Ethos API key can be
 *    perfectly valid and still unable to serve this lane, because what it may
 *    read is decided by which RESOURCES the college granted the application.
 *    Verify authenticates, then diffs the granted resources against the ones
 *    this lane needs. That diff — a per-resource checklist an operator works
 *    through — is the most important thing on the page, and nothing in the
 *    generic detail form can express it.
 * 2. **It is an API-key integration, not OAuth.** Paste a base URL + key
 *    (the Twilio pattern), never a "Connect with Ellucian" redirect: the
 *    credential authenticates an APPLICATION, not a person. So there is no
 *    connected account, no consent screen, and no Connect button.
 *
 * EVERYTHING RENDERED HERE COMES OFF THE WIRE. The checklist is built from the
 * verify result's `scopes` (granted) and `warnings` (missing) — which together
 * cover the full required set, so the UI never holds its own copy of the list —
 * and the pre-verify list is the served record's `scopes`. The two compliance
 * claims render from the payload's own text (`desc` / `docs.what_we_dont_sync`),
 * because those are the two sentences a university's compliance reviewer reads
 * first and they are asserted by backend tests; a hardcoded copy would drift
 * silently. The one exception is `ETHOS_RESOURCE_NOTES` below — explanatory
 * copy, marked ui-config, on the `scopeMeta()` precedent.
 */
import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { Btn, Card, Icon, Input, Pill, T, tx } from "@v0y4ge/jrni-ui";
import { ApiError, getIntegrationDetail, updateIntegrationConfig, verifyIntegration } from "@v0y4ge/api-client";
import { toast } from "../../shell/toast";
import { useT } from "../../lib/i18n";
import { friendlyDateTime } from "../../lib/datetime";

export const ETHOS_PROVIDER_ID = "ethos";

/**
 * Timestamps arrive in two shapes: real ISO instants from a verify result, and the
 * seed's relative strings ("3 min ago", "Today"). `friendlyDateTime` echoes what it
 * cannot parse for BOTH its date and time halves, which rendered "3 min ago · 3 min
 * ago" — so format only what is actually a date and pass anything else through.
 */
function whenLabel(value: string): string {
  if (!value) return "";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : friendlyDateTime(value);
}

/** The two config keys the contract's strict `EthosIntegrationConfig` declares — nothing else may be sent. */
const BASE_URL_KEY = "base_url";
const API_KEY_KEY = "api_key";

/* ui-config — per-resource PURPOSE copy only (the resource list itself always
 * comes from the payload). Sourced from the contract's own note on
 * ETHOS_REQUIRED_RESOURCES: `persons` resolves identity at SSO login;
 * `students` / `student-academic-programs` / `person-holds` are the eligibility
 * snapshot; `academic-levels` / `student-classifications` are the two cached
 * code tables the snapshot's GUID references join against. A resource with no
 * entry renders without a note rather than with an invented one. */
const ETHOS_RESOURCE_NOTES: Record<string, string> = {
  persons: "Resolves the signed-in student to their SIS record at SSO login.",
  students: "Eligibility snapshot — confirms an active student record.",
  "student-academic-programs": "Eligibility snapshot — program / major.",
  "person-holds": "Eligibility snapshot — active holds that gate booking.",
  "academic-levels": "Cached code table the snapshot's GUID references join against.",
  "student-classifications": "Cached code table the snapshot's GUID references join against.",
};

/* ── Payload reads (tolerant — never assume a field the producer may not send) ─ */
export interface EthosField {
  key: string; label: string; type: string; required: boolean;
  value: string; placeholder: string; description?: string;
  masked: boolean; configured: boolean;
}
export interface EthosRecord {
  provider: string; name: string; desc: string; auth: string;
  brand: string; icon: string; docs: string;
  fields: EthosField[];
  /** The resources this integration reads, as the record advertises them. */
  resources: string[];
  /** `docs.what_we_dont_sync` when the producer sends it — the compliance claims. */
  whatWeDontSync: string[];
  /** `docs.troubleshooting` when the producer sends it. */
  troubleshooting: { title: string; body: string }[];
  /** Verify history — `syncHistory` rows whose `kind` is "verify". */
  verifyHistory: { at: string; detail: string; outcome: string }[];
  /** Every `syncHistory` row, for the case where the producer sends activity but no verify rows. */
  history: { at: string; detail: string; outcome: string }[];
  requiresInput: boolean;
  missingFields: string[];
  enabled: boolean;
}

const str = (v: unknown, fallback = ""): string => (typeof v === "string" ? v : fallback);

/**
 * What a stored secret is allowed to LOOK like in the UI.
 *
 * The backend serves a masked credential back as `value: "\u2022\u2022\u2022\u2022\u2022\u20221234"`, and that is what
 * should paint. But a masked field is rendered read-only from whatever the producer sent,
 * and "the producer redacted it" is not something the screen can verify — a mock, a
 * replayed fixture or a future producer that echoes the raw key would put a live
 * credential on screen in plain text. So the display is masked HERE as well: anything
 * that is not already bullet-masked is reduced to bullets plus its last four characters.
 * Defence in depth, on the assumption that the value may be real.
 */
const SECRET_BULLET = "\u2022";
export function maskSecretForDisplay(value: string): string {
  if (!value) return "";
  if (value.indexOf(SECRET_BULLET) >= 0) return value; // already redacted upstream
  const tail = value.length > 4 ? value.slice(-4) : "";
  return SECRET_BULLET.repeat(6) + tail;
}
const arr = (v: unknown): Record<string, unknown>[] => (Array.isArray(v) ? v as Record<string, unknown>[] : []);
const strArray = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);

export function ethosFromResponse(raw: unknown): EthosRecord | null {
  const body = raw && typeof raw === "object" ? raw as Record<string, unknown> : null;
  if (!body) return null;
  const r = (body.data && typeof body.data === "object" ? body.data : body) as Record<string, unknown>;
  if (!r || str(r.provider ?? r.id) === "") return null;
  const docs = r.docs && typeof r.docs === "object" ? r.docs as Record<string, unknown> : null;
  const configuration = r.configuration && typeof r.configuration === "object" ? r.configuration as Record<string, unknown> : {};
  const fields = arr(r.configFields ?? r.inputs).map((f): EthosField => ({
    key: str(f.key),
    label: str(f.label ?? f.l ?? f.key),
    type: str(f.type, "text"),
    required: f.required === true,
    value: typeof f.value === "string" ? f.value : typeof f.filled === "string" ? f.filled : "",
    placeholder: str(f.placeholder),
    description: str(f.description) || undefined,
    // Contract rule: `masked === true` — NOT the type name — is the signal to
    // render a write-only credential input with a Change affordance. The two
    // facades disagree on the type name (`secret` vs `password`), never on this.
    masked: f.masked === true,
    configured: f.configured === true,
  })).filter((f) => f.key === BASE_URL_KEY || f.key === API_KEY_KEY);
  return {
    provider: str(r.provider ?? r.id),
    name: str(r.name, "Ellucian Ethos"),
    desc: str(r.desc ?? r.description),
    auth: str(r.auth),
    brand: str(r.brand, "#6244BB"),
    icon: str(r.icon),
    docs: typeof r.docs === "string" ? r.docs : str(docs?.summary),
    fields,
    resources: strArray(r.scopes ?? r.permissions),
    whatWeDontSync: strArray(docs?.what_we_dont_sync ?? docs?.whatWeDontSync),
    troubleshooting: arr(docs?.troubleshooting).map((row) => ({
      title: str(row.title ?? row.q ?? row.symptom),
      body: str(row.body ?? row.a ?? row.detail ?? row.fix),
    })).filter((row) => row.title || row.body),
    verifyHistory: arr(r.syncHistory).filter((row) => str(row.kind) === "verify").map((row) => ({
      at: str(row.occurredAt ?? row.at),
      detail: str(row.detail ?? row.e),
      outcome: str(row.outcome, "success"),
    })),
    history: arr(r.syncHistory).map((row) => ({
      at: str(row.occurredAt ?? row.at),
      detail: str(row.detail ?? row.e),
      outcome: str(row.outcome, "success"),
    })),
    requiresInput: configuration.requiresInput === true || r.needsInput === true,
    missingFields: strArray(configuration.missingRequiredFields),
    enabled: r.enabled !== false,
  };
}

/* ── Verify result ────────────────────────────────────────────────────────── */
export interface EthosVerify {
  ok: boolean;
  checkedAt: string;
  latencyMs: number | null;
  accountLabel: string;
  granted: string[];
  missing: string[];
  code: string;
  message: string;
  retriable: boolean;
  httpStatus: number | null;
}

export function ethosVerifyFrom(data: unknown): EthosVerify {
  const d = (data && typeof data === "object" ? data : {}) as Record<string, unknown>;
  const err = d.error && typeof d.error === "object" ? d.error as Record<string, unknown> : null;
  const account = d.account && typeof d.account === "object" ? d.account as Record<string, unknown> : null;
  const httpStatus = err && typeof err.http_status === "number" ? err.http_status : null;
  const retriable = err ? err.retriable === true : false;
  return {
    ok: d.ok === true,
    checkedAt: str(d.checked_at),
    latencyMs: typeof d.latency_ms === "number" ? d.latency_ms : null,
    accountLabel: str(account?.label),
    granted: strArray(d.scopes),
    missing: strArray(d.warnings),
    code: str(err?.code),
    message: str(err?.message),
    retriable,
    httpStatus,
  };
}

/** Design per `error.code` — NEVER per HTTP status (200 ok / 503 retriable / 502 not / 500 fault). */
type EthosOutcome = "success" | "grants" | "not-configured" | "credential" | "base-url" | "transient" | "generic";
export function ethosOutcome(v: EthosVerify | null): EthosOutcome {
  if (!v) return "generic";
  if (v.ok) return "success";
  switch (v.code) {
    case "MISSING_GRANTS": return "grants";
    case "NOT_CONFIGURED": return "not-configured";
    case "TOKEN_REJECTED": case "CREDENTIAL_DECRYPT_FAILED": return "credential";
    case "INVALID_BASE_URL": return "base-url";
    case "RATE_LIMITED": case "PROVIDER_UNREACHABLE": return "transient";
    default: return "generic";
  }
}

/* ── The state hook (one instance, owned by IntegrationDetail) ───────────── */
export interface EthosState {
  record: EthosRecord | null;
  loading: boolean;
  loadError: string | null;
  verify: EthosVerify | null;
  verifying: boolean;
  runVerify: () => Promise<void>;
  reload: () => Promise<void>;
  /** Per-field inline errors, keyed by config-field key (SSRF guard / validation). */
  fieldErrors: Record<string, string>;
  saveField: (key: string, value: string) => Promise<boolean>;
  savingKey: string | null;
}

export function useEthosState(active: boolean): EthosState {
  const t = useT();
  const [record, setRecord] = useState<EthosRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [verify, setVerify] = useState<EthosVerify | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const rec = ethosFromResponse(await getIntegrationDetail(ETHOS_PROVIDER_ID));
      if (rec) { setRecord(rec); setLoadError(null); }
      else setLoadError(t("settings.ethos.recordUnavailable", "The Ethos record couldn't be read."));
    } catch (e) {
      console.warn("getIntegrationDetail(ethos) unavailable:", e);
      setLoadError(e instanceof Error ? e.message : String(e));
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => {
    if (!active) { setLoading(false); return; }
    let alive = true;
    (async () => { await reload(); if (!alive) return; })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const runVerify = useCallback(async () => {
    if (verifying) return;
    setVerifying(true);
    try {
      const r = await verifyIntegration(ETHOS_PROVIDER_ID);
      const v = ethosVerifyFrom(r.data);
      setVerify(v);
      // Branch on the CODE. A configuration gap is not a failed request — it is a
      // checklist, so it gets a neutral-warning toast pointing at the panel, never
      // an error toast that implies retrying might help.
      if (v.ok) toast({ tone: "success", title: t("settings.ethos.verifiedTitle", "Verified"), subtitle: v.accountLabel || undefined });
      else if (v.code === "MISSING_GRANTS") toast({ tone: "warn", title: t("settings.ethos.missingGrantsToast", "Resource grants are missing"), subtitle: t("settings.ethos.missingGrantsToastSub", "Work through the checklist below, then verify again.") });
      else toast({ tone: "warn", title: t("settings.integrationDetail.verificationFailed", "Verification failed"), subtitle: v.message || undefined });
      await reload();
    } catch (e) {
      toast({ tone: "warn", title: t("settings.ethos.verifyFailedTitle", "Couldn't run the check"), subtitle: e instanceof Error ? e.message : String(e) });
    } finally { setVerifying(false); }
  }, [verifying, reload, t]);

  const saveField = useCallback(async (key: string, value: string): Promise<boolean> => {
    setSavingKey(key);
    setFieldErrors((prev) => { const next = { ...prev }; delete next[key]; return next; });
    try {
      // Strict body: ONLY the field being written. Omitting `api_key` keeps the
      // stored key, so a base-URL change never re-asks for the credential; an
      // empty string clears the field.
      await updateIntegrationConfig(ETHOS_PROVIDER_ID, { config: { [key]: value } });
      await reload();
      // A saved credential invalidates the previous preflight result — the
      // checklist described a configuration that no longer exists.
      setVerify(null);
      toast({ tone: "success", title: t("settings.ethos.savedTitle", "Saved"), subtitle: t("settings.ethos.savedSub", "Run Verify to check the key and the resource grants.") });
      return true;
    } catch (e) {
      const code = e instanceof ApiError ? e.code : "";
      const message = e instanceof Error ? e.message : String(e);
      // The SSRF destination guard rejects a base URL with a 400 VALIDATION_ERROR
      // whose message NAMES the field and gives a human reason. That belongs on the
      // input, not in a page-level failure the operator has to map back to a field.
      if (code === "VALIDATION_ERROR" && message.toLowerCase().includes(key)) {
        setFieldErrors((prev) => ({ ...prev, [key]: message }));
      } else if (code === "PROVIDER_SECRETS_UNREADABLE") {
        toast({ tone: "warn", title: t("settings.ethos.secretsUnreadableTitle", "Stored credentials can't be read"), subtitle: t("settings.ethos.secretsUnreadableSub", "Re-enter the API key — the encryption key was rotated.") });
      } else {
        setFieldErrors((prev) => ({ ...prev, [key]: message }));
      }
      return false;
    } finally { setSavingKey(null); }
  }, [reload, t]);

  return { record, loading, loadError, verify, verifying, runVerify, reload, fieldErrors, saveField, savingKey };
}

/** Connection state, derived the way the brief's status table derives it: evidence-based. */
export type EthosKind = "requires-input" | "configured-unverified" | "connected" | "disabled";
export function ethosKind(state: EthosState): EthosKind {
  const rec = state.record;
  if (!rec) return "requires-input";
  if (!rec.enabled) return "disabled";
  if (rec.requiresInput) return "requires-input";
  return state.verify?.ok ? "connected" : "configured-unverified";
}

/**
 * Client-side check on the Ethos Integration base URL.
 *
 * The server remains the authority — it runs the platform's SSRF destination guard,
 * because it is the party that will fetch this address with the tenant's credential
 * attached, and only it knows the blocked ranges. This mirrors that guard's documented
 * rules so an address that CANNOT be accepted is caught at the field instead of being
 * stored and then failing at verify: plain http, loopback / private / link-local
 * addresses, infrastructure-local hostnames, embedded credentials, and anything that is
 * not an absolute URL. Returns the reason, or null when the value may be sent.
 */
export function validateEthosBaseUrl(raw: string): string | null {
  const value = raw.trim();
  if (!value) return "Enter the base URL of your Ethos Integration hub.";
  let url: URL;
  try { url = new URL(value); } catch {
    return "That isn't a complete address. Enter the full URL starting with https:// — for example https://integrate.elluciancloud.com";
  }
  if (url.protocol !== "https:") {
    return url.protocol === "http:"
      ? "Plain http isn't allowed — the API key travels on this connection. Use https://"
      : "Only https addresses are allowed. Enter the URL starting with https://";
  }
  if (url.username || url.password) return "Remove the username and password from the URL — the API key below is the credential.";
  const host = url.hostname.toLowerCase().replace(/^\[|\]$/g, "");
  // A pasted "https://https://host" lands here: the parsed host is literally "https".
  const looksLikeIp = /^[0-9a-f:.]+$/i.test(host) && (host.indexOf(":") >= 0 || /^\d/.test(host));
  if (!host || (host.indexOf(".") < 0 && !looksLikeIp && host !== "localhost")) {
    return `"${host}" isn't a valid hostname. Check for a duplicated https:// or a typo.`;
  }
  if (host === "localhost" || host.endsWith(".localhost") || host === "::1") return "That address points at this machine, not at an Ethos hub.";
  if (/\.(internal|local|localdomain)$/.test(host) || host.endsWith(".home.arpa")) return "Infrastructure-local hostnames aren't allowed — enter the public Ethos Integration hub address.";
  const v4 = host.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (v4) {
    const a = Number(v4[1]), b = Number(v4[2]);
    if (a === 127 || a === 10 || a === 0 || (a === 192 && b === 168) || (a === 172 && b >= 16 && b <= 31) || (a === 169 && b === 254) || (a === 100 && b >= 64 && b <= 127)) {
      return "That's a private or link-local address — enter your Ethos Integration hub's public https address.";
    }
  }
  if (host.indexOf(":") >= 0 && /^(fc|fd|fe80)/i.test(host)) return "That's a private or link-local address — enter your Ethos Integration hub's public https address.";
  return null;
}

/* ═══ Panels ══════════════════════════════════════════════════════════════ */

const cardHead: CSSProperties = { padding: "12px 16px", borderBottom: `1px solid ${T.border}` };

/* ── Configuration: base URL + API key ───────────────────────────────────── */
function EthosConfigFieldInput({ f, canWrite, state }: { f: EthosField; canWrite: boolean; state: EthosState }) {
  const t = useT();
  // The contract's signal for the write-only CHANGE affordance is `masked === true`.
  // Whether a value may be PRINTED is a different question, and it is not one to answer
  // from a producer flag: the API key is a credential whatever the payload claims about
  // it, so the secret treatment (password input, lock, masked display) also engages on
  // the field's own type and key.
  const secret = f.masked || f.type === "password" || f.type === "secret" || f.key === API_KEY_KEY;
  const writeOnly = secret && f.configured;
  const [localErr, setLocalErr] = useState<string | null>(null);
  const [editing, setEditing] = useState(!writeOnly && !f.configured);
  const [val, setVal] = useState(writeOnly ? "" : f.value);
  const err = localErr ?? state.fieldErrors[f.key];
  const saving = state.savingKey === f.key;
  const label = `${f.label}${f.required ? " *" : ""}`;

  // Re-checked on every keystroke once something is typed, so Save is DISABLED on an
  // address the server cannot accept — rather than the operator discovering it at verify.
  const liveError = f.key === BASE_URL_KEY && val.trim().length > 0 ? validateEthosBaseUrl(val) : null;
  const blocked = liveError != null;

  const commit = async () => {
    if (f.key === BASE_URL_KEY) {
      const reason = validateEthosBaseUrl(val);
      if (reason) { setLocalErr(reason); return; }
    }
    setLocalErr(null);
    const ok = await state.saveField(f.key, val);
    if (ok && writeOnly) { setEditing(false); setVal(""); }
  };

  if (writeOnly && !editing) {
    return (
      <div>
        <label style={{ fontSize: 13, color: T.n1, fontWeight: 500, display: "block", marginBottom: 4 }}>{label}</label>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0, height: 36, padding: "0 10px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.n6, display: "flex", alignItems: "center", gap: 8, fontFamily: T.fontMono, fontSize: 13, color: T.n2 }}>
            <span style={{ color: T.soft, display: "inline-flex" }}><Icon.lock size={12} /></span>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{maskSecretForDisplay(f.value) || SECRET_BULLET.repeat(8)}</span>
          </div>
          {canWrite ? <Btn variant="secondary" size="sm" onClick={() => { setVal(""); setEditing(true); }}>{t("settings.integrationDetail.change", "Change")}</Btn> : null}
        </div>
        {f.description ? <p style={{ fontSize: 11, color: T.soft, margin: "4px 0 0", lineHeight: 1.5 }}>{f.description}</p> : null}
      </div>
    );
  }

  const dirty = writeOnly ? val.trim().length > 0 : val !== f.value;
  return (
    <div>
      <Input
        label={label}
        value={val}
        onChange={(e: any) => { setVal(e.target.value); if (localErr) setLocalErr(null); }}
        placeholder={writeOnly ? t("settings.integrationDetail.enterNewValue", "Enter a new value") : f.placeholder}
        type={secret ? "password" : "text"}
        leading={secret ? <Icon.lock size={12} /> : undefined}
        readOnly={!canWrite}
      />
      {err || blocked ? (
        <p style={{ fontSize: 11, color: T.red1, margin: "4px 0 0", lineHeight: 1.5, display: "flex", gap: 6, alignItems: "flex-start" }}>
          <span style={{ display: "inline-flex", marginTop: 1 }}><Icon.warn size={11} /></span>
          <span>{err || liveError}</span>
        </p>
      ) : f.description ? (
        <p style={{ fontSize: 11, color: T.soft, margin: "4px 0 0", lineHeight: 1.5 }}>{f.description}</p>
      ) : null}
      {canWrite ? (
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <Btn size="sm" disabled={saving || !dirty || blocked} onClick={() => { void commit(); }}>{saving ? t("settings.integrationDetail.saving", "Saving\u2026") : t("common.actions.save", "Save")}</Btn>
          {writeOnly ? <Btn variant="ghost" size="sm" disabled={saving} onClick={() => { setEditing(false); setVal(""); }}>{t("common.actions.cancel", "Cancel")}</Btn> : null}
        </div>
      ) : null}
    </div>
  );
}

export function EthosConfigCard({ state, canWrite }: { state: EthosState; canWrite: boolean }) {
  const t = useT();
  const rec = state.record;
  if (state.loading) return <Card style={{ padding: 24, fontSize: 12, color: T.soft }}>{t("settings.ethos.loading", "Loading the Ethos record\u2026")}</Card>;
  if (!rec) {
    return (
      <Card style={{ padding: 16, background: `color-mix(in srgb, ${T.red1} 7%, ${T.white})`, borderColor: `color-mix(in srgb, ${T.red1} 25%, transparent)` }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.red1 }}>{t("settings.ethos.noRecordTitle", "Ethos configuration is unavailable")}</div>
        <div style={{ fontSize: 12, color: T.n1, marginTop: 4 }}>{state.loadError || t("settings.ethos.noRecordSub", "The provider record didn't load, so no fields are shown \u2014 rather than an empty form that would look configurable.")}</div>
      </Card>
    );
  }
  return (
    <Card style={{ padding: 0 }}>
      <div style={cardHead}>
        <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("settings.ethos.configTitle", "Ethos application credentials")}</h3>
        <p style={{ ...tx.help, marginTop: 2 }}>{rec.docs}</p>
      </div>
      <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
        {rec.fields.map((f) => <EthosConfigFieldInput key={f.key} f={f} canWrite={canWrite} state={state} />)}
      </div>
    </Card>
  );
}

/* ── The grant checklist — Surface C, the reason this page exists ─────────── */
export function EthosGrantChecklist({ state, canWrite }: { state: EthosState; canWrite: boolean }) {
  const t = useT();
  const v = state.verify;
  const rec = state.record;
  const outcome = ethosOutcome(v);
  // Pre-verify: the required set as the RECORD advertises it, all unchecked.
  // Post-verify: `scopes` ∪ `warnings`, which by contract cover the full set —
  // so the checklist never needs its own copy of the list.
  const granted = v ? v.granted : [];
  const missing = v ? v.missing : [];
  const rows = v && (granted.length || missing.length)
    ? [...granted, ...missing]
    : rec?.resources ?? [];
  const known = v != null && (granted.length > 0 || missing.length > 0);

  const headline = !v
    ? t("settings.ethos.checklistIdle", "Not checked yet")
    : outcome === "success" ? (v.accountLabel || t("settings.ethos.allGranted", "All required resources granted"))
    : outcome === "grants" ? t("settings.ethos.grantsMissing", "Resource grants missing")
    : v.message;

  return (
    <Card style={{ padding: 0 }}>
      <div style={{ ...cardHead, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div>
          <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("settings.ethos.checklistTitle", "Required resource grants")}</h3>
          <p style={{ ...tx.help, marginTop: 2 }}>{t("settings.ethos.checklistHelp", "Verify authenticates the API key, then compares the resources your college granted the jrni application against the ones this lane reads.")}</p>
        </div>
        {!v ? <Pill tone="neutral">{t("settings.ethos.pillUnchecked", "Not checked")}</Pill>
          : outcome === "success" ? <Pill tone="success" dot>{t("settings.ethos.pillGranted", "All granted")}</Pill>
          : outcome === "grants" ? <Pill tone="warn">{t("settings.ethos.missingCount", "{count} missing", { count: missing.length })}</Pill>
          : <Pill tone="danger">{t("settings.ethos.pillNotChecked", "Check failed")}</Pill>}
      </div>

      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", gap: 10, alignItems: "flex-start", background: outcome === "success" && v ? T.green2 : outcome === "grants" ? T.orange2 : v ? `color-mix(in srgb, ${T.red1} 6%, ${T.white})` : T.n6 }}>
        <span style={{ display: "inline-flex", marginTop: 1, color: !v ? T.soft : outcome === "success" ? T.green1 : outcome === "grants" ? T.orange1 : T.red1 }}>
          {!v ? <Icon.info size={14} /> : outcome === "success" ? <Icon.check size={14} /> : <Icon.warn size={14} />}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{headline}</div>
          {v ? (
            <div style={{ fontSize: 11, color: T.soft, marginTop: 2 }}>
              {v.checkedAt ? whenLabel(v.checkedAt) : ""}
              {v.latencyMs != null ? t("settings.ethosIntegration.text2", "· {latencyMs} ms", { latencyMs: v.latencyMs }) : ""}
              {v.code ? t("settings.ethosIntegration.text3", "· {code}", { code: v.code }) : ""}
              {v.httpStatus != null ? t("settings.ethosIntegration.text4", "· HTTP {httpStatus}", { httpStatus: v.httpStatus }) : ""}
            </div>
          ) : (
            <div style={{ fontSize: 11, color: T.soft, marginTop: 2 }}>{t("settings.ethos.checklistIdleSub", "Run Verify to check the credentials and the grants together.")}</div>
          )}
        </div>
        {/* Retry only when the contract says retrying can help. MISSING_GRANTS is
            `retriable: false` — a configuration gap — so the operator makes the
            change in Ethos Integration first and verifies again deliberately. */}
        {canWrite ? (
          <Btn variant={outcome === "grants" || !v ? "secondary" : "ghost"} size="sm" icon={<Icon.refresh size={12} />} disabled={state.verifying} onClick={() => { void state.runVerify(); }}>
            {state.verifying ? t("settings.integrationDetail.verifying", "Verifying\u2026")
              : !v ? t("settings.integrationDetail.verify", "Verify")
              : v.retriable ? t("settings.integrationDetail.retry", "Retry")
              : t("settings.ethos.verifyAgain", "Verify again")}
          </Btn>
        ) : null}
      </div>

      {outcome === "not-configured" ? (
        <div style={{ padding: 16, fontSize: 12, color: T.n1 }}>{t("settings.ethos.notConfiguredBody", "No call was made \u2014 enter the base URL and API key above first.")}</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 16, fontSize: 12, color: T.soft }}>{t("settings.ethos.noResources", "The record advertises no resource list, so there is nothing to check against.")}</div>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: "4px 0" }}>
          {rows.map((resource) => {
            const isMissing = missing.indexOf(resource) >= 0;
            const isGranted = granted.indexOf(resource) >= 0;
            const note = ETHOS_RESOURCE_NOTES[resource];
            return (
              <li key={resource} style={{ display: "flex", gap: 10, padding: "10px 16px", borderTop: `1px solid ${T.border}`, alignItems: "flex-start" }}>
                <span style={{ display: "inline-flex", marginTop: 1, color: isGranted ? T.green1 : isMissing ? T.orange1 : T.n4, flexShrink: 0 }}>
                  {isGranted ? <Icon.check size={14} /> : isMissing ? <Icon.warn size={14} /> : <Icon.info size={14} />}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <code style={{ fontFamily: T.fontMono, fontSize: 12, color: T.n1 }}>{resource}</code>
                  {note ? <div style={{ fontSize: 11, color: T.soft, marginTop: 2, lineHeight: 1.5 }}>{note}</div> : null}
                  {isMissing ? (
                    <div style={{ fontSize: 12, color: T.n1, marginTop: 4, lineHeight: 1.5 }}>
                      {t("settings.ethos.grantPrefix", "Grant")}{" "}<code style={{ fontFamily: T.fontMono, fontSize: 11 }}>{resource}</code>{" "}{t("settings.ethos.grantSuffix", "to the jrni application in Ethos Integration.")}
                    </div>
                  ) : null}
                </div>
                {isGranted ? <Pill tone="success">{t("settings.ethos.rowGranted", "Granted")}</Pill>
                  : isMissing ? <Pill tone="warn">{t("settings.ethos.rowMissing", "Not granted")}</Pill>
                  : <Pill tone="neutral">{t("settings.ethos.rowUnchecked", "Unchecked")}</Pill>}
              </li>
            );
          })}
        </ul>
      )}

      {v && !v.ok && !known && outcome !== "not-configured" ? (
        <div style={{ padding: "12px 16px", borderTop: `1px solid ${T.border}`, fontSize: 12, color: T.n2, lineHeight: 1.6 }}>
          {outcome === "credential" ? t("settings.ethos.tokenRejectedBody", "The grant list was never read \u2014 re-copy the current key from the Ethos application (keys are revoked on rotation), then verify again.")
            : outcome === "base-url" ? t("settings.ethos.invalidBaseUrlBody", "The grant list was never read \u2014 correct the base URL above. It is region-specific: a key issued in one hub does not authenticate against another.")
            : outcome === "transient" ? t("settings.ethos.transientBody", "Ethos didn't answer, so the granted-resource list couldn't be read. Nothing is wrong with your configuration \u2014 retry.")
            : t("settings.ethos.genericBody", "The check didn't reach the resource list, so no grant can be confirmed either way.")}
        </div>
      ) : null}
    </Card>
  );
}

/* ── Right column: what the lane reads, and what it never does ───────────── */
export function EthosResourcesCard({ state }: { state: EthosState }) {
  const t = useT();
  const rec = state.record;
  const granted = state.verify?.granted ?? [];
  const missing = state.verify?.missing ?? [];
  const resources = granted.length || missing.length ? [...granted, ...missing] : rec?.resources ?? [];
  if (!resources.length) return null;
  return (
    <Card style={{ padding: 20 }}>
      {/* An Ethos API key is NOT scoped — these are resource GRANTS on the
          application, so the panel is labelled the way Twilio labels account
          resources, never "scopes". */}
      <div style={tx.label as CSSProperties}>{t("settings.ethos.resourcesTitle", "Resources this integration reads")}</div>
      <ul style={{ listStyle: "none", margin: "10px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
        {resources.map((r) => (
          <li key={r} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.n1 }}>
            <span style={{ display: "inline-flex", color: missing.indexOf(r) >= 0 ? T.orange1 : granted.indexOf(r) >= 0 ? T.green1 : T.n4 }}>
              {missing.indexOf(r) >= 0 ? <Icon.warn size={12} /> : <Icon.check size={12} />}
            </span>
            <code style={{ fontFamily: T.fontMono, fontSize: 11 }}>{r}</code>
          </li>
        ))}
      </ul>
      <p style={{ fontSize: 11, color: T.soft, margin: "10px 0 0", lineHeight: 1.5 }}>{t("settings.ethos.resourcesHint", "Read-only resource grants on the Ethos application \u2014 not OAuth scopes, and not consented by a person.")}</p>
    </Card>
  );
}

/**
 * The two claims a university's compliance reviewer reads first — that the lane never
 * writes to Banner, and that no student credentials beyond the match key are stored.
 * Both come down the wire and are asserted by backend tests, so they render FROM THE
 * PAYLOAD (`docs.what_we_dont_sync`); a hardcoded copy would drift silently.
 *
 * And only from there. This card previously fell back to `desc` when the producer sent
 * no claims — which listed what the integration DOES under a heading saying what it
 * never does. A panel that contradicts its own label is worse than an absent panel,
 * and this is the panel a compliance reviewer reads. So when the payload makes no
 * claims, none are shown; the read-only fact still reaches the page through `desc`, in
 * the page header and the identity card, where it reads correctly.
 */
export function EthosClaimsCard({ state }: { state: EthosState }) {
  const t = useT();
  const claims = state.record?.whatWeDontSync ?? [];
  if (!claims.length) return null;
  return (
    <Card style={{ padding: 20, background: T.blue2, borderColor: `color-mix(in srgb, ${T.blue1} 19%, transparent)` }}>
      <div style={{ ...(tx.label as CSSProperties), color: T.blue1 }}>{t("settings.ethos.claimsTitle", "What this integration never does")}</div>
      <ul style={{ listStyle: "none", margin: "10px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
        {claims.map((c, i) => (
          <li key={i} style={{ display: "flex", gap: 8, fontSize: 12, color: T.n1, lineHeight: 1.5 }}>
            <span style={{ display: "inline-flex", marginTop: 2, color: T.blue1, flexShrink: 0 }}><Icon.check size={12} /></span>
            <span>{c}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * Verification history comes off the detail payload (`syncHistory`) — there is no
 * separate call. A producer that sends activity rows but no `kind: "verify"` rows
 * gets the same list under its truthful heading rather than an empty panel.
 */
export function EthosVerifyHistoryCard({ state }: { state: EthosState }) {
  const t = useT();
  const verifyRows = state.record?.verifyHistory ?? [];
  const rows = verifyRows.length ? verifyRows : state.record?.history ?? [];
  if (!rows.length) return null;
  return (
    <Card style={{ padding: 0 }}>
      <div style={cardHead}><div style={tx.label as CSSProperties}>{verifyRows.length ? t("settings.ethos.historyTitle", "Verification history") : t("settings.integrationDetail.recentActivity", "Recent activity")}</div></div>
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {rows.map((r, i) => (
          <li key={i} style={{ padding: "10px 16px", borderTop: i ? `1px solid ${T.border}` : "none", display: "flex", gap: 8, justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ display: "flex", gap: 8, alignItems: "center", minWidth: 0 }}>
              <span style={{ width: 6, height: 6, borderRadius: 999, flexShrink: 0, background: r.outcome === "failure" ? T.orange1 : T.green1 }} />
              <span style={{ fontSize: 12, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.detail}</span>
            </span>
            <span style={{ fontSize: 11, color: T.soft, flexShrink: 0 }}>{whenLabel(r.at)}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function EthosTroubleshootingCard({ state }: { state: EthosState }) {
  const t = useT();
  const rows = state.record?.troubleshooting ?? [];
  const [open, setOpen] = useState<number | null>(0);
  if (!rows.length) return null;
  return (
    <Card style={{ padding: 0 }}>
      <div style={cardHead}>
        <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("settings.ethos.troubleshootingTitle", "Troubleshooting")}</h3>
        <p style={{ ...tx.help, marginTop: 2 }}>{t("settings.ethos.troubleshootingHelp", "The first two are what an operator hits during onboarding.")}</p>
      </div>
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {rows.map((r, i) => (
          <li key={i} style={{ borderTop: i ? `1px solid ${T.border}` : "none" }}>
            <button type="button" onClick={() => setOpen(open === i ? null : i)}
              style={{ width: "100%", display: "flex", gap: 8, alignItems: "center", padding: "10px 16px", background: "transparent", border: "none", cursor: "pointer", fontFamily: T.font, fontSize: 13, color: T.n1, textAlign: "left" }}>
              <span style={{ display: "inline-flex", color: T.soft }}><Icon.chev size={10} dir={open === i ? "down" : "right"} /></span>
              <span style={{ flex: 1, fontWeight: 500 }}>{r.title}</span>
            </button>
            {open === i && r.body ? <div style={{ padding: "0 16px 12px 34px", fontSize: 12, color: T.n2, lineHeight: 1.6 }}>{r.body}</div> : null}
          </li>
        ))}
      </ul>
    </Card>
  );
}
